// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/shared/components/ui";
import { interviewRulesFor } from "@/features/interview/interviewRules";

/**
 * Conduct rules the candidate must accept before their first interview for a
 * role. Deliberately NOT the shared <Modal>: this one cannot be dismissed with
 * Escape or a backdrop click, because declining is a real choice that has to go
 * through the Cancel button rather than an accidental tap.
 */
export function InterviewRulesModal({ isPhone, busy, error, onAccept, onCancel }) {
  const [checked, setChecked] = useState(false);
  const panelRef = useRef(null);
  const rules = interviewRulesFor(isPhone);
  const canStart = checked && !busy;

  useEffect(() => {
    panelRef.current?.focus?.();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Swallow Escape: closing must be an explicit choice, not a stray key.
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="interview-rules-title"
        tabIndex={-1}
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92dvh] flex flex-col safe-bottom outline-none"
      >
        <div className="px-5 pt-5 pb-3 border-b border-line">
          <h2 id="interview-rules-title" className="text-lg font-black text-ink-900">
            Before you start
          </h2>
          <p className="text-ink-400 text-sm mt-1">
            Please follow these during your interview so we capture your answers clearly.
          </p>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <ul className="space-y-3">
            {rules.map((r, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-base leading-6 shrink-0" aria-hidden="true">{r.icon}</span>
                <span className="text-sm text-ink-600 leading-6">{r.text}</span>
              </li>
            ))}
          </ul>

          <label className="flex items-start gap-3 mt-5 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 w-5 h-5 shrink-0 accent-brand-600"
            />
            <span className="text-sm font-bold text-ink-900">
              I have read these instructions and agree to follow them during my interview.
            </span>
          </label>

          {error ? <p className="text-red-600 text-xs mt-3">{error}</p> : null}
        </div>

        <div className="px-5 py-4 border-t border-line flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            Not now
          </Button>
          <Button type="button" loading={busy} disabled={!canStart} onClick={onAccept}>
            Agree &amp; start interview
          </Button>
        </div>
      </div>
    </div>
  );
}
